import { useLayoutEffect, useRef, useState } from 'react'

const measureLongestLine = (textElement: HTMLElement) => {
  const range = document.createRange()
  range.selectNodeContents(textElement)

  const lineWidths = Array.from(range.getClientRects()).map(
    (rect) => rect.width
  )
  return lineWidths.length > 0 ? Math.max(...lineWidths) : 0
}

const getHorizontalExtra = (element: HTMLElement) => {
  const style = getComputedStyle(element)
  return (
    parseFloat(style.paddingLeft) +
    parseFloat(style.paddingRight) +
    parseFloat(style.borderLeftWidth) +
    parseFloat(style.borderRightWidth)
  )
}

type UseFitToContentArgs = {
  maxWidth?: string
  measureKey?: string
}

/**
 * Shrinks a wrapping button so its width hugs the longest rendered text line
 * instead of staying at its `max-width` cap. Pure CSS can't do this: once the cap
 * forces a wrap, a `fit-content` box resolves to the cap and only redistributes
 * text within it. We let the button fall back to `fit-content` while measuring,
 * then return the resulting width to drive it. Only the text element is measured,
 * so this is intended for text-only buttons (no icon).
 */
export const useFitToContent = ({
  maxWidth,
  measureKey,
}: UseFitToContentArgs) => {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [fitWidth, setFitWidth] = useState<number>()

  useLayoutEffect(() => {
    const button = buttonRef.current
    if (!button || !maxWidth) {
      return undefined
    }

    const measure = () => {
      const textElement = button.lastElementChild
      if (!(textElement instanceof HTMLElement)) {
        return
      }

      // Neutralize the constrained width so the layout reflows against the natural
      // `fit-content` cap; reverted before paint, so it never shows.
      button.style.width = 'fit-content'
      const longestLine = measureLongestLine(textElement)
      const horizontalExtra = getHorizontalExtra(button)
      button.style.width = ''

      if (longestLine > 0) {
        setFitWidth(Math.ceil(longestLine + horizontalExtra))
      }
    }

    measure()

    const resizeObserver = new ResizeObserver(measure)
    if (button.parentElement) {
      resizeObserver.observe(button.parentElement)
    }

    let isActive = true
    void document.fonts?.ready.then(() => {
      if (isActive) {
        measure()
      }
    })

    return () => {
      isActive = false
      resizeObserver.disconnect()
    }
  }, [maxWidth, measureKey])

  return { buttonRef, fitWidth }
}
